import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, AlertTriangle, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { DateInputFR } from '../DateInputFR';

const VIDE = { type_intervenant: '', siret: false, contrat_portage: false, contrat_travail: false, convention: false, assurance: false, assurance_centre: false,
  assurance_echeance: '', diplomes: false, carte_pro: false, carte_pro_echeance: '', date_controle: '', commentaire: '' };
const ECHEANCES = { assurance: 'assurance_echeance', carte_pro: 'carte_pro_echeance' };
const auj = () => new Date().toISOString().slice(0, 10);
const fr = (iso) => iso.slice(0, 10).split('-').reverse().join('/');
const depuis = (data) => ({ ...VIDE, ...data.conformite, type_intervenant: data.conformite?.type_intervenant || data.bilan.type, date_controle: '' });

const Justificatif = ({ k, label, f, set, id }) => (
  <div className="flex flex-wrap items-center gap-2 text-sm">
    <label className="flex flex-1 items-center gap-2"><input type="checkbox" checked={!!f[k]} onChange={set(k)} className="h-4 w-4 accent-[#8DC63F]" data-testid={`conformite-${k}-${id}`} /> {label}</label>
    {ECHEANCES[k] && <span className="flex items-center gap-1 text-xs text-slate-500">échéance <DateInputFR value={f[ECHEANCES[k]]} onChange={set(ECHEANCES[k])} className="h-7 w-32 text-xs" data-testid={`conformite-${k}-echeance-${id}`} />
      {f[ECHEANCES[k]] && f[ECHEANCES[k]] < auj() && <span className="font-semibold text-red-600">expirée</span>}</span>}
  </div>
);

// Encadré « Conformité intervenant » (indicateur Qualiopi 27) : justificatifs attendus selon le type d'intervenant
export const ConformiteSousTraitant = ({ formateurId }) => {
  const [fiche, setFiche] = useState(null);
  const [f, setF] = useState(VIDE);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`/qualiopi/sous-traitants/${formateurId}`).then((r) => { setFiche(r.data); setF(depuis(r.data)); }).catch(() => setFiche(false));
  }, [formateurId]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? (v.target.type === 'checkbox' ? v.target.checked : v.target.value) : v }));
  const enregistrer = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(`/qualiopi/sous-traitants/${formateurId}`, f);
      setFiche(data); setF(depuis(data));
      toast.success('Contrôle de conformité enregistré');
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'enregistrement"); } finally { setBusy(false); }
  };
  if (fiche === false) return null;
  if (!fiche) return <div className="flex justify-center py-4 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  const c = fiche.conformite || {};
  const type = fiche.types[f.type_intervenant];
  return (
    <div className="border-t border-slate-200 pt-4" data-testid={`conformite-${formateurId}`} onClick={(e) => e.stopPropagation()}>
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase text-slate-500"><ShieldCheck className="h-3.5 w-3.5 text-[#8DC63F]" /> Conformité intervenant (Qualiopi ind. 27)</p>
      <select value={f.type_intervenant} onChange={set('type_intervenant')} className="mb-2 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid={`conformite-type-${formateurId}`}>
        <option value="">Type d'intervenant à préciser…</option>
        {Object.entries(fiche.types).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
      </select>
      {type && <p className={`mb-2 flex gap-1.5 rounded-lg px-2.5 py-2 text-xs ${type.concerne ? 'bg-blue-50 text-blue-800' : 'bg-slate-50 text-slate-600'}`} data-testid={`conformite-aide-${formateurId}`}>
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {type.aide}</p>}
      {type?.concerne && (
        <p className="mb-2 text-xs text-slate-500" data-testid={`conformite-lettres-${formateurId}`}>
          {fiche.avec_clause} lettre(s) signée(s) avec la clause Qualiopi
          {fiche.sans_clause > 0 && <span className="ml-1 inline-flex items-center gap-1 font-semibold text-amber-700"><AlertTriangle className="h-3 w-3" /> {fiche.sans_clause} signée(s) avant la clause : avenant ou nouvelle lettre à faire</span>}
        </p>
      )}
      <div className="space-y-1.5">
        {(type?.attendus || []).map((k) => <Justificatif key={k} k={k} label={fiche.justificatifs[k]} f={f} set={set} id={formateurId} />)}
      </div>
      <Textarea rows={2} value={f.commentaire} onChange={set('commentaire')} placeholder="Commentaire du contrôle" className="mt-2 text-sm" data-testid={`conformite-commentaire-${formateurId}`} />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-slate-500" data-testid={`conformite-dernier-${formateurId}`}>{c.date_controle ? `Dernier contrôle le ${fr(c.date_controle)} par ${c.modifie_par} (${(c.historique || []).length} au total)` : 'Jamais contrôlé'}</span>
        <Button size="sm" onClick={enregistrer} disabled={busy || !f.type_intervenant} className="h-7 bg-[#0F172A] text-xs text-white hover:bg-[#1e293b]" data-testid={`conformite-enregistrer-${formateurId}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Enregistrer le contrôle'}
        </Button>
      </div>
    </div>
  );
};
